import { APPLICATION_API_SCOPE, setApiRequestsBlocked } from "../api/apiRequestGate";

export type AppRuntimePhase = "pending" | "ready" | "blocked";
export type AppRuntimeBlockKind = "updating" | "unavailable";

export type AppRuntimeSnapshot = {
  phase: AppRuntimePhase;
  blockKind: AppRuntimeBlockKind;
  /** Bestätigter Ausfall; bleibt während des anschließenden Abgleichs erhalten. */
  recovering: boolean;
};

const listeners = new Set<() => void>();
let pendingApiRequests = 0;
let reloadBlockingApiRequests = 0;
const requestCompletionListeners = new Set<() => void>();

/** Schreibaktionen schützen; ersetzbare Leseabfragen dürfen einen Versionswechsel nicht festhalten. */
export function trackPendingApiRequest(method = "POST"): () => void {
  const blocksReload = !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
  pendingApiRequests++;
  if (blocksReload) reloadBlockingApiRequests++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    pendingApiRequests--;
    if (blocksReload) reloadBlockingApiRequests--;
    if (reloadBlockingApiRequests === 0) requestCompletionListeners.forEach(listener => listener());
  };
}

export function subscribeApiRequestsCompleted(listener: () => void): () => void {
  requestCompletionListeners.add(listener);
  return () => { requestCompletionListeners.delete(listener); };
}

export function hasPendingApiRequests(): boolean {
  return pendingApiRequests > 0;
}

export function hasReloadBlockingApiRequests(): boolean {
  return reloadBlockingApiRequests > 0;
}

let snapshot: AppRuntimeSnapshot = {
  phase: "pending",
  blockKind: "updating",
  recovering: false,
};

function publish(next: AppRuntimeSnapshot): void {
  if (snapshot.phase === next.phase && snapshot.blockKind === next.blockKind && snapshot.recovering === next.recovering) return;
  snapshot = next;
  listeners.forEach((listener) => listener());
}

export function getAppRuntimeSnapshot(): AppRuntimeSnapshot {
  return snapshot;
}

export function subscribeAppRuntime(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function signalAppRuntimeUnavailable(kind: AppRuntimeBlockKind = "updating"): void {
  setApiRequestsBlocked(APPLICATION_API_SCOPE, true);
  publish({ phase: "blocked", blockKind: kind, recovering: true });
}

export function signalAppRuntimeReady(): void {
  setApiRequestsBlocked(APPLICATION_API_SCOPE, false);
  publish({ phase: "ready", blockKind: snapshot.blockKind, recovering: false });
}

/** Backend erreichbar, aber Versions- oder Sitzungsabgleich noch nicht abgeschlossen. */
export function signalAppRuntimePending(): void {
  publish({ phase: "pending", blockKind: "updating", recovering: snapshot.recovering });
}

/** Keine zweite Fehlermeldung neben der laufenden Startup-Wiederherstellung. */
export function isAppRuntimeRecovering(): boolean {
  return snapshot.recovering;
}

export function isAppRuntimeReady(): boolean {
  return snapshot.phase === "ready";
}
